import { Box, Button, Chip, Typography } from "@mui/material";
import { API } from "aws-amplify";
import React from "react";
import { useUser } from "../contexts/useUser";
import CopyToClipboard from "react-copy-to-clipboard";
import { set } from "react-hook-form";

interface APIKeysProps {
  // Add any props you need for the component here
}

const APIKeys: React.FC<APIKeysProps> = () => {
  const { project, entitlements } = useUser();
  const [apiKey1, setApiKey1] = React.useState("");
  const [apiKey2, setApiKey2] = React.useState("");
  const [revoke1FirstClick, setRevoke1FirstClick] = React.useState(false);
  const [revoke2FirstClick, setRevoke2FirstClick] = React.useState(false);
  const [key1LastRotated, setKey1LastRotated] = React.useState("");
  const [key2LastRotated, setKey2LastRotated] = React.useState("");

  const [allowedPublicApi, setAllowedPublicApi] = React.useState<boolean>(true);

  /*
{
    key1: string;
    key1LastRotated : string;
    key2: string;
    key2LastRotated : string;
}
    */

  React.useEffect(() => {
    getAPIKeys();
  }, [project]);

  React.useEffect(() => {
    if (entitlements) {
      setAllowedPublicApi(entitlements.includes("public-api"));
    }
  }, [entitlements]);

  const getAPIKeys = () => {
    console.log(project?.id);

    API.get("eventapi", "/getIntegrationApiKeys", {
      queryStringParameters: {
        projectId: project?.id,
      },
    })
      .then((response) => {
        if (response.success) {
          const keys = JSON.parse(response.body);
          setApiKey1(keys.key1);
          setApiKey2(keys.key2);
          setKey1LastRotated(keys.key1LastRotated);
          setKey2LastRotated(keys.key2LastRotated);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const createAPIKeys = async () => {
    const response = await API.post("eventapi", "/createIntegrationApiKeys", {
      body: {
        projectId: project?.id,
        projectName: project?.name,
      },
    });

    if (response.success) {
      const keys = JSON.parse(response.body);
      setApiKey1(keys.key1);
      setApiKey2(keys.key2);
      setKey1LastRotated(keys.key1LastRotated);
      setKey2LastRotated(keys.key2LastRotated);
    }
  };

  const rotateKey = async (keyname: string) => {
    try {
      const response = await API.post("eventapi", "/rotateIntegrationApiKey", {
        body: {
          projectId: project?.id,
          key: keyname,
        },
      });

      if (response.success) {
        const keys = JSON.parse(response.body);
        setApiKey1(keys.key1);
        setApiKey2(keys.key2);
        setKey1LastRotated(keys.key1LastRotated);
        setKey2LastRotated(keys.key2LastRotated);
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box
      p={2}
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gap={2}
    >
      <Typography variant="h5" color="HighlightText">
        API Keys for Customer API Access{" "}
        <Chip
          size="small"
          label="ent"
          title="Customer management API is available at enterprise level"
        />
      </Typography>
      <Typography
        variant="body2"
        color={allowedPublicApi ? "HighlightText" : "InactiveCaptionText"}
      >
        To make secure external calls via our API for Zapier or Stripe, you can
        use either of these API keys and the project id to authenticate.
      </Typography>

      {!project && <Typography variant="body2">loading...</Typography>}

      {project && (
        <>
          {apiKey1 === "" && apiKey2 === "" && (
            <Button
              variant="contained"
              color="primary"
              onClick={async () => await createAPIKeys()}
              disabled={!allowedPublicApi}
            >
              Create API Keys for this Project
            </Button>
          )}

          <>
            <Box display="flex" alignItems="flex-start">
              <Typography
                variant="body2"
                color={
                  allowedPublicApi ? "HighlightText" : "InactiveCaptionText"
                }
              >
                Key 1:{" "}
                {apiKey1 && apiKey1 !== ""
                  ? `${apiKey1.slice(0, 2)}${apiKey1
                      .slice(2, -2)
                      .replace(/./g, "*")}${apiKey1.slice(-2)}`
                  : "loading..."}
                &nbsp;&nbsp;
              </Typography>
              <CopyToClipboard text={apiKey1}>
                <Button
                  variant="contained"
                  disabled={!allowedPublicApi || !apiKey1 || apiKey1 === ""}
                >
                  Copy
                </Button>
              </CopyToClipboard>

              <Button
                variant={revoke1FirstClick ? "contained" : "text"}
                color="secondary"
                onClick={() => {
                  if (revoke1FirstClick) {
                    rotateKey("key1");
                  }
                  setRevoke1FirstClick(!revoke1FirstClick);
                }}
                disabled={!allowedPublicApi}
              >
                {revoke1FirstClick
                  ? "This action isn't reversible, confirm?"
                  : "revoke (rotate)"}
              </Button>
              {revoke1FirstClick && (
                <Button
                  variant="text"
                  color="secondary"
                  onClick={() => setRevoke1FirstClick(false)}
                >
                  Cancel
                </Button>
              )}
            </Box>
            <Box display="flex" alignItems="flex-start">
              <Typography
                variant="body2"
                color={
                  allowedPublicApi ? "HighlightText" : "InactiveCaptionText"
                }
              >
                Key 2:{" "}
                {apiKey2 && apiKey2 !== ""
                  ? `${apiKey2.slice(0, 2)}${apiKey2
                      .slice(2, -2)
                      .replace(/./g, "*")}${apiKey2.slice(-2)}`
                  : "loading..."}
                &nbsp;&nbsp;
              </Typography>
              <CopyToClipboard text={apiKey2}>
                <Button
                  variant="contained"
                  disabled={!allowedPublicApi || !apiKey2 || apiKey2 === ""}
                >
                  Copy
                </Button>
              </CopyToClipboard>
              <Button
                variant={revoke2FirstClick ? "contained" : "text"}
                color="secondary"
                onClick={() => {
                  if (revoke2FirstClick) {
                    rotateKey("key2");
                  }
                  setRevoke2FirstClick(!revoke2FirstClick);
                }}
                disabled={!allowedPublicApi}
              >
                {revoke2FirstClick
                  ? "This action isn't reversible, confirm?"
                  : "revoke (rotate)"}
              </Button>
              {revoke2FirstClick && (
                <Button
                  variant="text"
                  color="secondary"
                  onClick={() => setRevoke2FirstClick(false)}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </>

          <Box display="flex" alignItems="flex-start">
            <Typography variant="body2">
              &nbsp;&nbsp; Project ID: {`${project?.id}`}
              &nbsp;&nbsp; &nbsp;&nbsp;
            </Typography>
            <CopyToClipboard text={project?.id ?? ""}>
              <Button variant="contained">Copy</Button>
            </CopyToClipboard>
          </Box>
        </>
      )}
    </Box>
  );
};

export default APIKeys;
